import { useState } from 'react';
import { Line } from 'react-chartjs-2';
import { Pencil, Plus, Target, Trash2 } from 'lucide-react';
import { Variacion, fmtNum, fmtPos, urlCorta } from './seoUtils';

function MiniTendencia({ tendencia }) {
  const puntos = (tendencia || []).map((t) => t.position);
  if (!puntos.some((p) => p != null)) {
    return <span className="text-xs text-gray-400">Sin datos aún</span>;
  }
  return (
    <div className="h-10 w-32">
      <Line
        data={{
          labels: tendencia.map((t) => t.fecha),
          datasets: [{
            data: puntos,
            borderColor: '#1A9169',
            borderWidth: 1.5,
            pointRadius: 0,
            tension: 0.3,
            spanGaps: true,
          }],
        }}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (ctx) => `Posición ${fmtPos(ctx.parsed.y)}`,
              },
            },
          },
          scales: { x: { display: false }, y: { display: false, reverse: true } },
        }}
      />
    </div>
  );
}

function FormularioKeyword({ inicial, onGuardar, onCancelar, editando }) {
  const [keyword, setKeyword] = useState(inicial?.keyword || '');
  const [pagina, setPagina] = useState(inicial?.pagina_objetivo || '');
  const [notas, setNotas] = useState(inicial?.notas || '');
  const [guardando, setGuardando] = useState(false);

  const enviar = async (e) => {
    e.preventDefault();
    if (!editando && !keyword.trim()) return;
    setGuardando(true);
    try {
      await onGuardar({ keyword: keyword.trim(), pagina_objetivo: pagina.trim() || null, notas: notas.trim() || null });
    } catch (err) {
      // el error se muestra en la cabecera de la página
    } finally {
      setGuardando(false);
    }
  };

  return (
    <form onSubmit={enviar} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-2 items-end bg-gray-50 border rounded-md p-3 mb-3">
      <label className="text-xs text-gray-600">
        Palabra clave
        <input
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          disabled={editando}
          placeholder="catering para empresas madrid"
          className="mt-1 w-full px-2 py-1.5 text-sm border rounded-md disabled:bg-gray-100"
        />
      </label>
      <label className="text-xs text-gray-600">
        Página que queremos posicionar (opcional)
        <input
          value={pagina}
          onChange={(e) => setPagina(e.target.value)}
          placeholder="https://duettocatering.com/..."
          className="mt-1 w-full px-2 py-1.5 text-sm border rounded-md"
        />
      </label>
      <label className="text-xs text-gray-600">
        Notas (opcional)
        <input
          value={notas}
          onChange={(e) => setNotas(e.target.value)}
          className="mt-1 w-full px-2 py-1.5 text-sm border rounded-md"
        />
      </label>
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={guardando}
          className="px-3 py-1.5 text-sm bg-duetto-primary text-white rounded-md hover:opacity-90 disabled:opacity-50"
        >
          {guardando ? 'Guardando…' : 'Guardar'}
        </button>
        <button type="button" onClick={onCancelar} className="px-3 py-1.5 text-sm border rounded-md hover:bg-gray-100">
          Cancelar
        </button>
      </div>
    </form>
  );
}

/**
 * Tarjeta "Palabras clave objetivo": seguimiento diario de las búsquedas que nos importan.
 */
export default function KeywordsObjetivo({ items, sugerencias, onCrear, onEditar, onBorrar, cargando }) {
  const [formNuevo, setFormNuevo] = useState(false);
  const [editandoId, setEditandoId] = useState(null);

  return (
    <div className="bg-white border rounded-lg p-5 mb-6">
      <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
        <h2 className="text-base font-semibold text-gray-700 flex items-center gap-2">
          <Target className="w-4 h-4 text-duetto-primary" />
          Palabras clave objetivo
        </h2>
        {!formNuevo && (
          <button
            type="button"
            onClick={() => { setFormNuevo(true); setEditandoId(null); }}
            className="px-3 py-1.5 text-sm border rounded-md hover:bg-gray-50 flex items-center gap-1"
          >
            <Plus className="w-4 h-4" /> Añadir
          </button>
        )}
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Las búsquedas que queremos ganar. Cada noche se guarda su posición en Google. La posición actual es la media
        de los últimos 7 días (1 = primero) y la flecha la compara con la semana anterior.
      </p>

      {formNuevo && (
        <FormularioKeyword
          onGuardar={async (payload) => { await onCrear(payload); setFormNuevo(false); }}
          onCancelar={() => setFormNuevo(false)}
        />
      )}

      {cargando && !items.length && <div className="text-sm text-gray-400">Cargando…</div>}

      {!cargando && items.length === 0 && (
        <div className="border border-dashed rounded-md p-4">
          <p className="text-sm text-gray-700 mb-2">
            Aún no hay palabras clave objetivo. Propuesta según el plan SEO (pulsa para añadirlas una a una):
          </p>
          <div className="flex flex-wrap gap-2">
            {(sugerencias || []).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => onCrear({ keyword: s }).catch(() => {})}
                className="px-2.5 py-1 text-xs rounded-full border bg-white hover:bg-emerald-50 hover:border-emerald-300 flex items-center gap-1"
              >
                <Plus className="w-3 h-3" /> {s}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-2">También puedes añadirlas desde la tabla de consultas con el botón Seguir.</p>
        </div>
      )}

      {items.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-500 border-b">
                <th className="text-left font-medium py-2">Palabra clave</th>
                <th className="text-right font-medium">Posición actual</th>
                <th className="text-left font-medium pl-4">Últimos 28 días</th>
                <th className="text-right font-medium">Impresiones 7 días</th>
                <th className="text-right font-medium">Clics 7 días</th>
                <th className="text-left font-medium pl-4">Página que sale en Google</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((k) => {
                const variacion = k.posicion_actual != null && k.posicion_semana_anterior != null
                  ? Math.round((k.posicion_semana_anterior - k.posicion_actual) * 10) / 10
                  : null;
                const mejor = k.mejor_pagina?.page;
                const paginaDistinta = k.pagina_objetivo && mejor && k.pagina_objetivo.replace(/\/$/, '') !== mejor.replace(/\/$/, '');
                if (editandoId === k.id) {
                  return (
                    <tr key={k.id}>
                      <td colSpan={7} className="pt-2">
                        <FormularioKeyword
                          editando
                          inicial={k}
                          onGuardar={async (payload) => { await onEditar(k.id, payload); setEditandoId(null); }}
                          onCancelar={() => setEditandoId(null)}
                        />
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr key={k.id} className="border-b last:border-0 align-middle">
                    <td className="py-2">
                      <div className="font-medium text-gray-800">{k.keyword}</div>
                      {k.notas && <div className="text-xs text-gray-500">{k.notas}</div>}
                    </td>
                    <td className="text-right whitespace-nowrap">
                      {k.posicion_actual != null ? (
                        <>
                          <span className="font-semibold">{fmtPos(k.posicion_actual)}</span>{' '}
                          <Variacion valor={variacion} tipo="posicion" />
                        </>
                      ) : (
                        <span className="text-xs text-gray-400" title="Sin impresiones en los últimos 7 días">No aparece</span>
                      )}
                    </td>
                    <td className="pl-4"><MiniTendencia tendencia={k.tendencia} /></td>
                    <td className="text-right">{fmtNum(k.impresiones_7d)}</td>
                    <td className="text-right">{fmtNum(k.clics_7d)}</td>
                    <td className="pl-4 text-xs font-mono">
                      {mejor ? (
                        <a href={mejor} target="_blank" rel="noopener noreferrer" className="hover:underline">{urlCorta(mejor)}</a>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                      {paginaDistinta && (
                        <div className="text-[11px] font-sans text-amber-700" title={k.pagina_objetivo}>
                          No es la página objetivo ({urlCorta(k.pagina_objetivo)})
                        </div>
                      )}
                    </td>
                    <td className="text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => { setEditandoId(k.id); setFormNuevo(false); }}
                        className="p-1 text-gray-400 hover:text-gray-700"
                        title="Editar"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => onBorrar(k)}
                        className="p-1 text-gray-400 hover:text-red-600"
                        title="Dejar de seguir"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
